/* istanbul ignore file */

/*
 * Testing this file with unit tests isn't meaningful, as it's cropping, rotating, and zooming an image.
 *
 * To get meaningful results from this test, we would need to inspect images and compare them to expected results.
 * This will be covered in automated UI tests.
 */

import {Area} from "react-easy-crop/types";
import {
  getImageEditorOptions,
  ImageEditorOptions
} from "../components/molecules/imageEditor/imageEditorOptions";

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues
    image.src = url;
    return image;
  });

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height)
  };
}

export default async function getCroppedImage(
  imageSrc: string,
  pixelCrop: Area,
  rotation: number,
  options?: ImageEditorOptions
): Promise<File> {
  const {maxSizeInPixels, maxFileSizeInBytes} = getImageEditorOptions(options);

  const image = await createImage(imageSrc);
  let canvas = rotateImage(image, rotation);
  canvas = cropImage(canvas, pixelCrop);

  if (Math.max(canvas.width, canvas.height) > maxSizeInPixels) {
    canvas = await scaleImage(canvas, maxSizeInPixels);
  }

  // Export to a file, with no compression
  let blob = await exportBlobFromCanvas(canvas);

  // Resize output file if it is too large
  if (blob.size > maxFileSizeInBytes) {
    const quality = maxFileSizeInBytes / blob.size;
    blob = await exportBlobFromCanvas(canvas, quality);
  }

  // filename is transient, so can be hardcoded
  return new File([blob], "cropped.jpeg", {type: "image/jpeg"});
}

function exportBlobFromCanvas(
  canvas: HTMLCanvasElement,
  quality: number = 1
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (file) => {
        if (file) resolve(file);
        reject(new Error("Could not convert canvas to blob"));
      },
      "image/jpeg",
      quality
    );
  });
}

function rotateImage(image: HTMLImageElement, rotation: number) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const {width: bBoxWidth, height: bBoxHeight} = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // start with plain white background
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);
  return canvas;
}

function cropImage(image: HTMLCanvasElement, pixelCrop: Area) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas;
}

async function scaleImage(image: HTMLCanvasElement, maxSizeInPixels: number) {
  // get the scale
  const scale = Math.min(
    maxSizeInPixels / image.width,
    maxSizeInPixels / image.height
  );

  const width = image.width * scale;
  const height = image.height * scale;

  const resizeWidth = width >> 0;
  const resizeHeight = height >> 0;

  const ibm = await window.createImageBitmap(
    image,
    0,
    0,
    image.width,
    image.height,
    {
      resizeWidth,
      resizeHeight,
      resizeQuality: "medium"
    }
  );
  const canvas = document.createElement("canvas");
  canvas.width = resizeWidth;
  canvas.height = resizeHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(ibm, 0, 0);
  return canvas;
}
